/**
 * Protected Route Component
 * Wraps routes that require authentication
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingAnimation from '../common/LoadingAnimation/LoadingAnimation';

/**
 * ProtectedRoute Component
 * Redirects to login if user is not authenticated
 */
export function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Returns the best available route given current user permissions.
 * Falls back down the chain until it finds something accessible.
 */
function getBestAvailablePath(userInfo) {
  if (!userInfo) return '/assortment';
  if (userInfo.isReportsCreated) return '/dashboard';
  if (userInfo.isOrdersCreated) return '/reports';
  if (userInfo.isAssortmentCreated) return '/orders';
  return '/assortment';
}

/**
 * PermissionRoute Component
 * Redirects to the best available page when the user lacks permission.
 * Pass `allowed={false}` to block access.
 */
export function PermissionRoute({ allowed, children }) {
  const { userInfo, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!allowed) {
    return <Navigate to={getBestAvailablePath(userInfo)} replace />;
  }

  return <>{children}</>;
}
